import { SlidersHorizontal } from 'lucide-react'
import { ButtonGlyph, buttonClass, Link } from '../../core/index.js'
import type { StaffingRequirement } from '../types.js'
import { SHIFT_TYPE_LABELS } from './preferencesText.js'
import { FLAG_TEXT, describeShiftSeats, formatHppd, hppdStanding } from './censusCardData.js'

/** Where the staffing plan is set: the Census group of the Scheduling tab on this facility's Configuration. */
export function staffingPlanHref(configHref: string): string {
  return `${configHref}?tab=scheduling&sub=census`
}

/** The way to the staffing plan, beside a line saying there is none; shown only where the screen knows the facility's Configuration. */
export function SetStaffingPlanLink({ configHref }: { configHref: string }) {
  return (
    <Link to={staffingPlanHref(configHref)} className={buttonClass({ size: 'sm' })}>
      <ButtonGlyph icon={SlidersHorizontal} size="sm" />
      Set the staffing plan
    </Link>
  )
}

const STANDING_CLASS = { above: 'text-marine', below: 'text-coral', at: 'text-green', none: 'text-ink-mid' } as const

/**
 * What a census requires on a unit, read-only: the HPPD line (planned
 * against target), a row per shift type with the seats by role, and the
 * traces on request. Shared by the Census tool, the What-if, the admin
 * dialog and the Partner Simulator. Given `configHref`, a missing plan
 * comes with the button to set it.
 */
export function CensusRequirementView({ requirement, showTraces = false, configHref }: { requirement: StaffingRequirement; showTraces?: boolean; configHref?: string }) {
  const standing = hppdStanding(requirement)
  return (
    <div className="text-[11px]">
      <div className={`font-bold ${STANDING_CLASS[standing]}`}>
        PPD {requirement.census} · planned {formatHppd(requirement.hppd.planned)}
        {requirement.hppd.target && ` against ${formatHppd(requirement.hppd.target)}`}
        {requirement.seats > 0 && <span className="font-normal text-ink-mid"> · {requirement.seats} seats, {requirement.hours} h</span>}
      </div>
      {requirement.shifts.length > 0 && (
        <ul className="mt-1 divide-y divide-marine/10">
          {requirement.shifts.map((shift) => (
            <li key={shift.shiftType} className="py-1">
              <span className="font-bold text-ink">{SHIFT_TYPE_LABELS[shift.shiftType]}</span> <span className="text-ink-mid">({shift.shiftHours} h)</span>: {describeShiftSeats(shift)}
              {showTraces && (
                <ul className="ml-3 mt-0.5 text-[10px] text-ink-mid">
                  {shift.roles.map((role) => (
                    <li key={role.role}>
                      {role.role}: {role.trace.join(' → ')}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      {requirement.flags.map((flag) => (
        <div key={flag} className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="text-[10px] text-coral">{FLAG_TEXT[flag]}</p>
          {flag === 'no_staffing_plan' && configHref && <SetStaffingPlanLink configHref={configHref} />}
        </div>
      ))}
    </div>
  )
}
